export interface ExternalIdData {
  name: string;
  icon: string;
  id: 'raw' | 'uri' | 'alnum';
  urls: {
    base?: () => string;
    person?: (id: string) => string;
    project?: (id: string) => string;
    orga?: (id: string) => string;
    work?: (id: string) => string;
    funder?: (id: string) => string;
  };
}

function load(url: string): string {
  try{
    return require(`${url}`);
  }
  catch(e){
    console.error(`Missing external id icon: ${url}`);
    return null;
  }
}

export interface ExternalIdDataCollection {
  [id: string]: ExternalIdData;
}

export const ExternalIdsData: ExternalIdDataCollection = {
  orcid: {
    name: 'ORCID',
    icon: load('./img/orcid_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://orcid.org',
      person: (id: string): string => `https://orcid.org/${id}`,
    },
  },
  gnd: {
    name: 'GND',
    icon: load('./img/gnd_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://d-nb.info/gnd',
      person: (id: string): string => `https://d-nb.info/gnd/${id}`,
      project: (id: string): string => `https://d-nb.info/gnd/${id}`,
      orga: (id: string): string => `https://d-nb.info/gnd/${id}`,
    },
  },
  scopus: {
    name: 'Scopus',
    icon: load('./img/scopus_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://www.scopus.com',
      person: (id: string): string => `https://www.scopus.com/authid/detail.uri?authorId=${id}&origin=recordpage`,
      work: (id: string): string => `https://www.scopus.com/record/display.uri?eid=${id}&origin=inward`,
    },
  },
  wos: {
    name: 'Web of Science',
    icon: load('./img/wos_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://www.webofscience.com',
      work: (id: string): string => `https://www.webofscience.com/wos/woscc/full-record/WOS:${id}`,
      person: (id: string): string => `https://www.webofscience.com/wos/author/record/${id}`,
    },
  },
  researcher: {
    name: 'Web of Science ID',
    icon: load('./img/wos_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://www.webofscience.com',
      person: (id: string): string => `https://www.webofscience.com/wos/author/record/${id}`,
    },
  },
  pmid: {
    name: 'PubMed',
    icon: load('./img/pmid_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://pubmed.ncbi.nlm.nih.gov/',
      work: (id: string): string => `https://pubmed.ncbi.nlm.nih.gov/${id}/`,
    },
  },
  arxiv: {
    name: 'Arxiv',
    icon: load('./img/arxiv_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://arxiv.org',
      person: (id: string): string => `https://arxiv.org/a/${id}`,
    },
  },
  isni: {
    name: 'ISNI ID',
    icon: load('./img/isni_icon.gif'),
    id: 'alnum',
    urls: {
      base: (): string => 'https://isnii.org/',
      project: (alnumid: string): string => `https://isni.org/isni/${alnumid}`,
      orga: (alnumid: string): string => `https://isni.org/isni/${alnumid}`,
      funder: (alnumid: string): string => `https://isni.org/isni/${alnumid}`,
    },
  },
  grid: {
    name: 'GRID',
    icon: load('./img/grid_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://grid.ac',
      project: (id: string): string => `https://grid.ac/institutes/grid.${id}`,
      orga: (id: string): string => `https://www.grid.ac/institutes/grid.${id}`,
      funder: (id: string): string => `https://www.grid.ac/institutes/grid.${id}`,
    },
  },
  grid_id: {
    name: 'GRID',
    icon: load('./img/grid_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://grid.ac',
      project: (id: string): string => `https://grid.ac/institutes/grid.${id}`,
      orga: (id: string): string => `https://www.grid.ac/institutes/grid.${id}`,
      funder: (id: string): string => `https://www.grid.ac/institutes/grid.${id}`,
    },
  },
  ror: {
    name: 'ROR',
    icon: load('./img/ror_icon.svg'),
    id: 'alnum',
    urls: {
      base: (): string => 'https://ror.org',
      project: (alnumid: string): string => `https://ror.org/${alnumid}`,
      orga: (alnumid: string): string => `https://ror.org/${alnumid}`,
      funder: (alnumid: string): string => `https://ror.org/${alnumid}`,
    },
  },
  ringgold: {
    name: 'Ringgold ID',
    icon: load('./img/ringgold_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://ido.ringgold.com',
      project: (id: string): string => `https://ido.ringgold.com/search/results?simple=${id}`,
      orga: (id: string): string => `https://ido.ringgold.com/search/results?simple=${id}`,
    },
  },
  crossref: {
    name: 'Crossref',
    icon: load('./img/crossref_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://www.crossref.org/',
      funder: (id: string): string => `https://search.crossref.org/search/works?q=${id}&from_ui=yes`,
    },
  },
  doi: {
    name: 'DOI',
    icon: load('./img/doi_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://doi.org/',
      work: (id: string): string => `https://doi.org/${id}`,
    },
  },
  isbn: {
    name: 'ISBN',
    icon: load('./img/isbn_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://de.wikipedia.org/wiki/Spezial:ISBN-Suche',
      work: (id: string): string => `https://de.wikipedia.org/wiki/Spezial:ISBN-Suche?isbn=${id}`,
    },
  },
  issn: {
    name: 'ISSN',
    icon: load('./img/issn_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://portal.issn.org/',
      work: (id: string): string => `https://portal.issn.org/resource/ISSN/${id}`,
    },
  },
  ismn: {
    name: 'ISMN',
    icon: load('./img/ismn_icon.gif'),
    id: 'uri',
    urls: {
      base: (): string => 'https://www.ismn-international.org/',
      work: (id: string): string => 'https://www.ismn-international.org/directory',
    },
  },
  hbz: {
    name: 'HBZ',
    icon: load('./img/hbz_icon.gif'),
    id: 'uri',
    urls: {
      base: () => 'https://www.hbz-nrw.de/',
    },
  },
  urn: {
    name: 'URN',
    icon: null,
    id: 'uri',
    urls: {
      base: (): string => 'https://nbn-resolving.org',
      work: (id: string): string => `https://nbn-resolving.org/${id}`,
    },
  },
  handle: {
    name: 'HANDLE',
    icon: null,
    id: 'uri',
    urls: {
      base: (): string => 'https://hdl.handle.net',
      work: (id: string): string => `https://hdl.handle.net/${id}`,
    },
  },
  zdb: {
    name: 'ZDB',
    icon: load('./img/zdb_icon.png'),
    id: 'uri',
    urls: {
      base: (): string => 'https://zdb-katalog.de/index.xhtml',
      work: (id: string): string => `https://ld.zdb-services.de/resource/${id}`,
    },
  },
  openalex: {
    name: 'OpenAlex',
    icon: load('./img/openAlex_icon.svg'),
    id: 'uri',
    urls: {
      base: (): string => 'https://openalex.org/',
      work: (id: string): string => `https://openalex.org/works/${id}`,
      person: (id: string): string => `https://openalex.org/authors/${id}`
    },
  }
};
